import { setStatus } from './global';
import { socketManager } from './network';

// Modal open/close
const fileBtn = document.getElementById('file-btn') as HTMLButtonElement;
const fileModal = document.getElementById('file-modal') as HTMLElement;
const fileClose = document.getElementById('file-modal-close') as HTMLButtonElement;

function modalOpen(): void {
  fileModal.classList.remove('hidden');
}

function modalClose(): void {
  fileModal.classList.add('hidden');
}

fileBtn.addEventListener('click', modalOpen);
fileClose.addEventListener('click', modalClose);
fileModal.addEventListener('click', (e: MouseEvent) => {
  if (e.target === fileModal) modalClose();
});

// Elements
const localFileInput = document.getElementById('local-file') as HTMLInputElement;
const remotePathInput = document.getElementById('remote-path') as HTMLInputElement;
const uploadBtn = document.getElementById('upload-btn') as HTMLButtonElement;
const transferStatus = document.getElementById('transfer-status') as HTMLElement;

interface TransferCardUI {
  wrapper: HTMLDivElement;
  pctEl: HTMLElement;
  barEl: HTMLElement;
  metaEl: HTMLElement;
  cancelBtn: HTMLButtonElement;
}

// UI card builder
function addTransferCard(kind: string, path: string): TransferCardUI {
  const wrapper = document.createElement('div');
  wrapper.className = 'rounded border border-gray-700 p-2';
  wrapper.innerHTML = `
    <div class="flex justify-between text-sm">
      <span class="font-mono">${kind}:${path}</span>
      <span class="pct text-gray-400">0%</span>
    </div>
    <div class="mt-2 h-2 bg-gray-700 rounded overflow-hidden">
      <div class="h-full bg-blue-600" style="width:0%"></div>
    </div>
    <div class="flex items-center py-2">
      <div class="meta text-xs text-gray-400">Speed: 0 MB/s • ETA: calculating...</div>
      <button class="ml-auto bg-red-600 hover:bg-red-700 text-white px-3 rounded">Cancel</button>
    </div>
  `;
  transferStatus?.prepend(wrapper);
  return {
    wrapper,
    pctEl: wrapper.querySelector('.pct') as HTMLElement,
    barEl: wrapper.querySelector('.h-full') as HTMLElement,
    metaEl: wrapper.querySelector('.meta') as HTMLElement,
    cancelBtn: wrapper.querySelector('button') as HTMLButtonElement,
  };
}

function updateTransferCard(ui: TransferCardUI, total: number, sent: number, startedAt: number): void {
  const pct = Math.floor((sent / total) * 100);
  ui.pctEl.textContent = `${pct}%`;
  ui.barEl.style.width = `${pct}%`;
  const dt = performance.now() - startedAt;
  const speedMBs = (sent * 1000) / ((dt * 1024 * 1024) || 1);
  const remaining = total - sent;
  const etaSec = speedMBs > 0 ? (remaining / (1024 * 1024)) / speedMBs : Infinity;
  ui.metaEl.textContent = total === sent
    ? `Speed: ${speedMBs.toFixed(2)} MB/s • Time: ${(dt / 1000).toFixed(2)}s • Completed`
    : `Speed: ${speedMBs.toFixed(2)} MB/s • ETA: ${isFinite(etaSec) ? Math.round(etaSec) + 's' : '—'}`;
}

async function* fileToUint8ArrayChunksStream(file: File, chunkSize: number = 0x8000): AsyncGenerator<Uint8Array> {
  let offset = 0;
  while (offset < file.size) {
    const blobChunk = file.slice(offset, offset + chunkSize);
    const ab = await blobChunk.arrayBuffer();
    yield new Uint8Array(ab);
    offset += chunkSize;
  }
}

// Upload
uploadBtn.addEventListener('click', async () => {
  const file = localFileInput.files?.[0];
  const path = remotePathInput.value.trim() || '';

  if (!file || !/^\/.+/.test(path)) {
    alert('Pick a local file and specify an absolute Linux path.');
    return;
  }

  const id = crypto.randomUUID();
  const size = file.size;
  const startedAt = performance.now();
  const cardCtl = addTransferCard('upload', path);

  let canceled = false;
  cardCtl.cancelBtn.addEventListener('click', () => {
    canceled = true;
  }, { once: true });

  setStatus(`Uploading ${file.name} → ${path} ...`, 'text-blue-400');
  socketManager.send('upload', { id, path, size });

  let seq = 0;
  let sent = 0;
  for await (const u8a of fileToUint8ArrayChunksStream(file)) {
    if (canceled) break;

    const b64 = btoa(String.fromCharCode(...u8a));

    socketManager.send('upload', { id, seq, b64 });
    seq += 1;
    sent += u8a.length;

    updateTransferCard(cardCtl, file.size, sent, startedAt);
  }
  cardCtl.cancelBtn.remove();

  if (canceled) {
    setStatus(`Upload canceled: ${file.name}`, 'text-red-500');
  } else {
    setStatus(`Upload completed: ${file.name}`, 'text-green-500');
  }
});

// export { modalOpen, modalClose };
